import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, relTime } from '../lib/api'

/**
 * Deliverability tester ("mail-tester"). Get one or more one-time addresses, send a real email to
 * each, and we score how likely it is to reach the inbox — DKIM verified against DNS, SPF and DMARC
 * alignment, the sending IP against blocklists, plus content signals. Because this runs inside the
 * logged-in dashboard, every received result is saved to the account's history below. It only
 * reads inbound test mail; it never sends anything.
 */
type Slot = { id: number; token: string; address: string; status: 'loading' | 'idle' | 'checking' | 'received' | 'empty' | 'error'; report?: any }
const MAX = 5
let seq = 0

export function MailTest() {
  const qc = useQueryClient()
  const [slots, setSlots] = useState<Slot[]>([])
  const [started, setStarted] = useState(false)

  const history = useQuery({ queryKey: ['mailtest', 'history'], queryFn: api.mailtest.history })

  function patch(id: number, p: Partial<Slot>) {
    setSlots(prev => prev.map(s => s.id === id ? { ...s, ...p } : s))
  }

  async function addSlot() {
    if (slots.length >= MAX) return
    const id = ++seq
    setSlots(prev => [...prev, { id, token: '', address: '', status: 'loading' }])
    setStarted(true)
    try {
      const j = await api.mailtest.newAddress()
      patch(id, { token: j.token, address: j.address, status: 'idle' })
    } catch { patch(id, { status: 'error' }) }
  }

  function removeSlot(id: number) {
    setSlots(prev => prev.filter(s => s.id !== id))
  }

  async function check(slot: Slot) {
    if (!slot.token) return
    patch(slot.id, { status: 'checking' })
    try {
      const j = await api.mailtest.check(slot.token)
      if (j.received) { patch(slot.id, { status: 'received', report: j }); qc.invalidateQueries({ queryKey: ['mailtest', 'history'] }) }
      else patch(slot.id, { status: 'empty' })
    } catch { patch(slot.id, { status: 'error' }) }
  }

  function checkAll() { slots.forEach(s => { if (s.status !== 'received') check(s) }) }

  const scoreColor = (s: number) => s >= 8 ? 'var(--success)' : s >= 6 ? 'var(--warning)' : 'var(--danger)'

  return (
    <div>
      <h1>Deliverability tester</h1>
      <p className="lede">
        Send a real email to a one-time address and we'll score how likely it is to reach the inbox —
        DKIM, SPF, DMARC, blocklists and content, out of 10. Test several senders at once; every
        result is saved to your history below.
      </p>

      {!started && (
        <div className="card">
          <button onClick={addSlot}>Start a test</button>
        </div>
      )}

      {started && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="section-label" style={{ margin: 0 }}>1. Send an email to each address</div>
            <button className="secondary small" onClick={addSlot} disabled={slots.length >= MAX}>
              {slots.length >= MAX ? `Max ${MAX}` : '+ Add address'}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {slots.map((s, i) => (
              <div key={s.id} className="row" style={{ gap: 10, alignItems: 'center', padding: '8px 0', borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <span className="badge">{i + 1}</span>
                <code className="mono" style={{ flex: 1, fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}>
                  {s.address || 'generating…'}
                </code>
                <span className="small">
                  {s.status === 'received' ? <span className="pill ok">✓ {s.report.score}/10</span>
                    : s.status === 'empty' ? <span className="pill warn">not received yet</span>
                    : s.status === 'checking' ? <span className="muted">checking…</span>
                    : s.status === 'error' ? <span className="pill warn">error</span>
                    : <span className="muted">waiting</span>}
                </span>
                {s.address && <button className="secondary small" onClick={() => navigator.clipboard?.writeText(s.address)}>Copy</button>}
                {s.token && <button className="small" onClick={() => check(s)}>Check</button>}
                {slots.length > 1 && <button className="ghost small" onClick={() => removeSlot(s.id)} title="Remove">✕</button>}
              </div>
            ))}
          </div>

          <div className="row" style={{ gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button onClick={checkAll}>Check all now</button>
            <span className="small muted">Send from the real system/inbox you want to test. Each address is valid ~30 minutes.</span>
          </div>
        </div>
      )}

      {/* live results for this session */}
      {slots.filter(s => s.status === 'received' && s.report).map(s => (
        <div key={s.id}>
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 10 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 700, lineHeight: 1, color: scoreColor(s.report.score) }}>{s.report.score}</div>
              <div className="small muted">out of 10</div>
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600 }}>{s.report.verdict}</div>
              <div className="small muted" style={{ marginTop: 4 }}>
                From <span className="mono">{s.report.from}</span>{s.report.subject ? ` · ${s.report.subject}` : ''}
                {s.report.sending_ip && <> · IP <span className="mono">{s.report.sending_ip}</span></>}
              </div>
            </div>
          </div>
          <div className="card flush" style={{ marginBottom: 18 }}>
            <table className="data">
              <tbody>
                {s.report.checks.map((c: any, i: number) => (
                  <tr key={i}>
                    <td style={{ width: 40, textAlign: 'center', fontSize: 18 }}>{c.ok ? '✅' : '⚠️'}</td>
                    <td className="strong">{c.label}</td>
                    <td className="small muted" style={{ textAlign: 'right' }}>{c.detail ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* saved history */}
      <h2 style={{ fontSize: 18, marginTop: 26, marginBottom: 10 }}>Past tests</h2>
      <div className="card flush">
        <table className="data">
          <thead>
            <tr><th>When</th><th>Score</th><th>From</th><th>Subject</th><th>DKIM</th><th>SPF</th><th>DMARC</th><th>Sending IP</th></tr>
          </thead>
          <tbody>
            {(history.data?.data ?? []).map((r: any) => (
              <tr key={r.id}>
                <td className="small muted" title={r.createdAt}>{relTime(r.createdAt)}</td>
                <td><span className="strong" style={{ color: scoreColor(r.score) }}>{r.score}</span><span className="small muted">/10</span></td>
                <td className="small mono">{r.fromAddress || r.fromDomain || '—'}</td>
                <td className="small" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.subject}>{r.subject || '—'}</td>
                <td>{pass(r.dkim)}</td>
                <td>{pass(r.spf)}</td>
                <td>{pass(r.dmarc)}</td>
                <td className="small mono">{r.sendingIp || '—'}</td>
              </tr>
            ))}
            {!history.isLoading && (history.data?.data?.length ?? 0) === 0 && (
              <tr><td colSpan={8} className="small muted" style={{ textAlign: 'center', padding: 24 }}>
                No past tests yet — run one above and it’ll be saved here.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function pass(v?: string) {
  if (v === 'pass') return <span className="pill ok">pass</span>
  if (v === 'fail') return <span className="pill warn">fail</span>
  return <span className="small muted">—</span>
}
